import React, { useRef, useEffect, useState, useMemo } from "react";
import moment from "moment";
import "moment/locale/ja";
import { ReactComponent as IconRepeat } from '@assets/Icons/IconRepeat.svg';


export const makeCustomAgendaDate = (holidays: string[]) => (props: any) => {
  const dateObj = props.day || props.date || props.value || props; // fallback
  const fullDate = moment(dateObj).format("YYYY-MM-DD");
  const isHoliday = holidays.some(h => h === fullDate);
  const isToday = moment(dateObj).isSame(moment(), 'day');

  // Debug log
  // console.log(
  //   `[AgendaDate] ${fullDate} — isHoliday: ${isHoliday}`,
  //   isHoliday ? '🟢' : '⚪️'
  // );

  const dayNumStr = moment(dateObj).locale("ja").format("DD");
  const hasLeadingZero = dayNumStr.startsWith("0");
  const displayDayNum = hasLeadingZero ? (
    <>
      <span style={{ 
        color: isHoliday ? "#E1FFDB" : "white",
        fontWeight: "normal"
      }}>0</span>
      <span style={{
        color: isToday ? "red" : undefined,
        fontWeight: isToday ? "bold" : "normal"
      }}>{dayNumStr[1]}</span>
    </>
  ) : (
    <span style={{
      color: isToday ? "red" : undefined,
      fontWeight: isToday ? "bold" : "normal"
    }}>{dayNumStr}</span>
  );

  const weekday = moment(dateObj).locale("ja").format("dd");

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering other calendar clicks
    
    const customEvent = new CustomEvent('agendaAddClick', {
      detail: {
        date: dateObj,
        dateString: fullDate
      }
    });
    document.dispatchEvent(customEvent);
  };

  return (
    <span data-date={fullDate}>
      <span style={{
        fontWeight: "normal",
        fontSize:"14px",
        color: undefined,
        background: isHoliday ? "#E1FFDB" : undefined,
        borderRadius: isHoliday ? "50px" : undefined,
        padding: isHoliday ? "2px 6px" : undefined,
        marginLeft: isHoliday ? "-6px" : undefined,
        }}>
          <button
          onClick={handleAddClick}
          style={{
            position: "relative",
            top:3,
            left:0,
            zIndex: 10,
            background: "transparent",
            color: "#ff6a0c",
            border: "none",
            borderRadius: "50%",
            width: "23px",
            height: "18px",
            fontSize: "24px",
            fontWeight: "bold",
            lineHeight: "14px",
            cursor: "pointer",
            textAlign: "left"
          }}
        >
          +
        </button>
        {displayDayNum}
      </span>
      <span style={{
        fontWeight: "normal",
        fontSize:"14px",
        color: "darkgray",
        marginLeft: isHoliday ? "1px" : "6px",
        }}>
        {weekday}
      </span>
    </span>
  );
};

function stripAlpha(hex) {
  if (/^#([0-9a-fA-F]{8})$/.test(hex)) {
      return '#' + hex.slice(1, 7);
    }
    return hex;
  }


export const makeCustomAgendaEvent = (holidays: string[]) => (props: any) => {
  const { event } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [addMargin, setAddMargin] = useState(false);

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      // For debugging:
      // console.log("event left:", rect.left);
      if (rect.left < 40) {
        setAddMargin(true);
      }
    }
  }, []);

  // detect if the event is on a holiday
  const eventDate = event.startDate || event.start || event.date;
  const formatted = moment(eventDate).format("YYYY-MM-DD");
  const isHoliday = holidays?.some(h => h === formatted);

  let backgroundColor = event.hexColor || undefined;
  backgroundColor = stripAlpha(backgroundColor);

  const isMultiDay = event.startDate && event.endDate && !moment(event.startDate).isSame(moment(event.endDate), 'day');

  let displayTime;

  if (isMultiDay) {
    const startDateTimeStr = moment(event.startDate).format("M/D H:mm");
    const endDateTimeStr = moment(event.endDate).format("M/D H:mm");
    displayTime = `${startDateTimeStr} - ${endDateTimeStr}`;
  } else {
 
    const startTimeFormatted = event.startTime ? moment(event.startTime, "HH:mm:ss").format("H:mm") : "";
    const endTimeFormatted = event.endTime ? moment(event.endTime, "HH:mm:ss").format("H:mm") : "";
    if (startTimeFormatted && endTimeFormatted) {
      displayTime = `${startTimeFormatted} - ${endTimeFormatted}`;
    } else {
      displayTime = startTimeFormatted || endTimeFormatted;
    }
  }

  const isRecurring = event.isRecurring;

  if (event.emptyEvent) {
    return (
      <div style={{
        // none
       }}>
        
      </div>
    );
  }

  return (
    <div
      ref={ref}
      style={{
        position: "relative",
        maxWidth: "100%",
      }}
    >
      {/* Invisible click blocker for margin area */}
      {addMargin && (
        <div
          style={{
            position: "absolute",
            left: "-79px",
            top: 0,
            width: "79px",
            height: "100%",
            cursor: "default",
            zIndex: 1,
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        />
      )}

    <div
      ref={ref}
      style={{
        background: backgroundColor,
        borderRadius: "6px",
        opacity: 1,
        border: "0px",
        fontSize: "12px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "4px 7px",
        textAlign: "left",
        maxWidth: "100%",
        cursor: "pointer",
          position: "relative",
          zIndex: 2,
      }}
    >
      {displayTime && (
        <span style={{ fontWeight: "bold", display: "flex", alignItems: "center" }}>
          {isRecurring && (
            <IconRepeat 
              style={{ 
                width: "14px", 
                height: "14px", 
                marginRight: "4px",
              }} 
            />
          )}
          {displayTime}
        </span>
      )}
      <span style={{
        fontWeight:"normal",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "flex",
        alignItems: "center",
        width: "100%",
        }}>
           {!displayTime && isRecurring && (
             <IconRepeat 
              style={{ 
                width: "14px", 
                height: "14px", 
                marginRight: "4px", 
                flexShrink: 0
              }} 
            />
          )}
          {event.title.length > 30 ? event.title.slice(0, 30) + '…' : event.title}
          </span>
    </div>
  </div>
  );
};


export const useBrowser = () => {
  const [browser, setBrowser] = useState('Unknown');

  useEffect(() => {
    const ua = navigator.userAgent;

    if (/CriOS/.test(ua)) {
      setBrowser('iOS Chrome');
    } else if (/FxiOS/.test(ua)) {
      setBrowser('iOS Firefox');
    } else if (/EdgiOS/.test(ua)) {
      setBrowser('iOS Edge');
    } else if (/iP(hone|ad|od)/.test(ua)) {
      setBrowser('iOS Safari');
    } else if (/Android/.test(ua) && /Chrome/.test(ua)) {
      setBrowser('Android Chrome');
    } else if (/Chrome/.test(ua)) {
      setBrowser('Chrome');
    } else if (/Firefox/.test(ua)) {
      setBrowser('Firefox');
    } else if (/Safari/.test(ua)) {
      setBrowser('Safari');
    } else {
      setBrowser('Unknown');
    }
  }, []);

  return browser;
};




export const useDeviceDetection = () => {
  const deviceInfo = useMemo(() => {
    const userAgent = navigator.userAgent;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const pixelRatio = window.devicePixelRatio || 1;
    
    // Detect platform
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);
    
    // Browser detection
    let browser = "Unknown";
    let platform = "Unknown";
    
    if (isIOS) {
      platform = "iOS";
      if (/CriOS/i.test(userAgent)) {
        browser = "iOS Chrome";
      } else if (/Safari/i.test(userAgent) && !/CriOS/i.test(userAgent)) {
        browser = "iOS Safari";
      } else if (/FxiOS/i.test(userAgent)) {
        browser = "iOS Firefox";
      }
    } else if (isAndroid) {
      platform = "Android";
      if (/Chrome/i.test(userAgent) && !/Edge/i.test(userAgent)) {
        browser = "Android Chrome";
      } else if (/Firefox/i.test(userAgent)) {
        browser = "Android Firefox";
      } else if (/SamsungBrowser/i.test(userAgent)) {
        browser = "Samsung Browser";
      } else if (/Opera|OPR/i.test(userAgent)) {
        browser = "Android Opera";
      } else {
        browser = "Android Browser";
      }
    } else {
      platform = "Desktop";
      if (/Chrome/i.test(userAgent) && !/Edge/i.test(userAgent)) {
        browser = "Chrome";
      } else if (/Firefox/i.test(userAgent)) {
        browser = "Firefox";
      } else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
        browser = "Safari";
      } else if (/Edge/i.test(userAgent)) {
        browser = "Edge";
      }
    }
    
    // Create a unique identifier for the screen
    const portraitIdentifier = `${Math.min(screenWidth, screenHeight)}x${Math.max(screenWidth, screenHeight)}@${pixelRatio}`;
    
    let deviceModel = "Unknown Device";
    let deviceCategory = "Unknown";
    
    if (isIOS) {
      // iPhone detection based on logical resolution and pixel ratio
      const iPhoneModels: { [key: string]: string } = {
        // iPhone 14 Pro Max, 15 Pro Max
        "430x932@3": "iPhone Pro Max (14/15)",
        
        // iPhone 14 Pro, 15 Pro
        "393x852@3": "iPhone Pro (14/15)",
        
        // iPhone 14 Plus, 15 Plus, 13 Pro Max, 12 Pro Max
        "428x926@3": "iPhone Plus/Pro Max (12/13/14/15)",
        
        // iPhone 14, 15, 13, 12, 13 Pro, 12 Pro
        "390x844@3": "iPhone Standard/Pro (12/13/14/15)",
        
        // iPhone 13 mini, 12 mini, 11 Pro, XS, X
        "375x812@3": "iPhone mini/Pro (X/XS/11/12/13)",
        
        // iPhone 11 Pro Max, XS Max
        "414x896@3": "iPhone Pro Max (XS/11)",
        
        // iPhone 11, XR
        "414x896@2": "iPhone (11/XR)",
        
        // iPhone SE (2nd & 3rd gen), 8, 7, 6s, 6
        "375x667@2": "iPhone SE/8/7/6",
        
        // iPhone SE (1st gen), 5s, 5c, 5
        "320x568@2": "iPhone SE (1st)/5",
      };
      
      if (iPhoneModels[portraitIdentifier]) {
        deviceModel = iPhoneModels[portraitIdentifier];
        deviceCategory = "iPhone";
      }
    } else if (isAndroid) {
      // Android device detection
      // Extract device info from user agent when possible
      const androidMatch = userAgent.match(/Android\s?([0-9.]*)/);
      const androidVersion = androidMatch ? androidMatch[1] : "Unknown";
      
      // Try to extract device model from user agent
      const deviceMatch = userAgent.match(/\(([^)]+)\)/);
      const deviceInfo = deviceMatch ? deviceMatch[1] : "";
      
      // Common Android devices by screen resolution
      const androidModels: { [key: string]: string } = {
        // Samsung Galaxy S24 Ultra, S23 Ultra, S22 Ultra
        "480x1032@3.75": "Galaxy S Ultra",
        "480x1037@3.75": "Galaxy S Ultra",
        
        // Samsung Galaxy S24+, S23+, S22+
        "450x973@3": "Galaxy S Plus",
        
        // Samsung Galaxy S24, S23, S22
        "411x915@3": "Galaxy S Series",
        "412x915@3": "Galaxy S Series",
        
        // Google Pixel 9 Pro XL, 8 Pro, 7 Pro
        "448x997@3.5": "Pixel Pro XL",
        "412x847@3.5": "Pixel Pro",
        
        // Google Pixel 9, 8, 7, 6
        "412x915@2.625": "Pixel Standard",
        "411x914@2.625": "Pixel Standard",
        
        // OnePlus 12, 11, 10
        "480x1096@3": "OnePlus Flagship",
        
        // Xiaomi 14, 13, 12
        "446x990@3.2": "Xiaomi Flagship",
        
        // Common mid-range resolutions
        "412x892@2.625": "Android Mid-range",
        "393x873@2.75": "Android Mid-range",
        "360x800@3": "Android Mid-range",
        
        // Common budget resolutions
        "360x780@2": "Android Budget",
        "360x760@2": "Android Budget",
      };
      
      if (androidModels[portraitIdentifier]) {
        deviceModel = androidModels[portraitIdentifier];
      } else {
        // Fallback: categorize by screen size
        const minDimension = Math.min(screenWidth, screenHeight);
        if (minDimension >= 480) {
          deviceModel = "Android Large";
        } else if (minDimension >= 400) {
          deviceModel = "Android Medium";
        } else {
          deviceModel = "Android Compact";
        }
      }
      
      deviceCategory = "Android";
      deviceModel += ` (Android ${androidVersion})`;
    }
    
    // Determine device characteristics
    const isProMax = platform === "iOS" && portraitIdentifier === "430x932@3";
    const isPro = platform === "iOS" && portraitIdentifier === "393x852@3";
    const isLargeAndroid = platform === "Android" && Math.min(screenWidth, screenHeight) >= 450;
    const isMediumAndroid = platform === "Android" && Math.min(screenWidth, screenHeight) >= 400 && Math.min(screenWidth, screenHeight) < 450;
    const isCompactDevice = Math.min(screenWidth, screenHeight) < 380;
    
    return {
      browser,
      platform,
      deviceModel,
      deviceCategory,
      screenWidth,
      screenHeight,
      pixelRatio,
      portraitIdentifier,
      
      // iOS specific
      isIOS,
      isProMax,
      isPro,
      isPlus: deviceModel.includes("Plus"),
      isMini: deviceModel.includes("mini"),
      is14ProMax: isProMax, // Specifically 14/15 Pro Max
      is14Pro: isPro, // Specifically 14/15 Pro
      
      // Android specific
      isAndroid,
      isLargeAndroid,
      isMediumAndroid,
      isGalaxy: deviceModel.includes("Galaxy"),
      isPixel: deviceModel.includes("Pixel"),
      
      // General characteristics
      isCompactDevice,
      isTablet: Math.min(screenWidth, screenHeight) >= 600,
      needsExtraPadding: isProMax || isLargeAndroid,
    };
  }, []);
  
  return deviceInfo;
};


export const useDynamicPadding = () => {
  const deviceInfo = useDeviceDetection();
  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  
  // Determine padding based on device
  let paddingBottom = "1.5rem"; // Default desktop padding
  
  if (!isMobile) {
    return paddingBottom; // Desktop always gets 1.5rem
  }
  
  // Mobile-specific padding logic
  if (deviceInfo.isIOS) {
    switch (deviceInfo.browser) {
      case "iOS Chrome":
        if (deviceInfo.is14ProMax) {
          paddingBottom = "10rem"; // More padding for Pro Max OK
        } else if (deviceInfo.isCompactDevice) {
          paddingBottom = "9.8rem"; // OK
        } else {
          paddingBottom = "1rem"; // OK
        }
        break;
        
      case "iOS Safari":
        if (deviceInfo.is14ProMax) {
          paddingBottom = "9rem"; // More padding for Pro Max OK
        } else if (deviceInfo.isCompactDevice) {
          paddingBottom = "6rem";
        } else {
          paddingBottom = "9rem";
        }
        break;
        
      default:
        paddingBottom = "5rem";
    }
  } else if (deviceInfo.isAndroid) {
    // Android devices generally need less padding adjustment
    if (deviceInfo.isLargeAndroid) {
      paddingBottom = "6rem";
    } else if (deviceInfo.isCompactDevice) {
      paddingBottom = "4rem";
    } else {
      paddingBottom = "5rem";
    }
  } else {
    // Unknown mobile devices
    paddingBottom = "5rem";
  }
  
  return paddingBottom;
};
